// Goals Galore — FOOTBALL workspace. "More than 3 goals."
// Two tiers (International / Clubs), each with Over 3.5 (headline "THE GALORE")
// + Over 2.5 (secondary "THE SAFER LINE") sub-views. Pure selector over
// predictions we already produce, swarm-vetted. Honest-empty when nothing
// clears the bar. UI: Break The Bank layout (masthead · seg switch · panels).

import { Component, useState, type ReactNode, type JSX } from "react";
import { useGoalsGalore, type GGPick, type GGTier } from "../../goals-galore/useGoalsGalore.ts";
import "../../styles/btb.css";

type Tier = "international" | "club";

// ── Icons (BTB glyph set) ──
function Ico({ d, size = 16 }: { d: ReactNode; size?: number }): JSX.Element {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{d}</svg>;
}
const GIC = {
  inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.5 5h13l3.5 7v6a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-6z" /></>,
};

// ── Error boundary — contain a render throw to a card, not the whole app ──
class GgBoundary extends Component<{ children: ReactNode }, { error: boolean }> {
  override state = { error: false };
  static getDerivedStateFromError(): { error: boolean } { return { error: true }; }
  override componentDidCatch(e: Error): void { console.error("[GoalsGalore] render error:", e); }
  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="state-wrap">
          <div className="state-icon"><Ico d={GIC.inbox} size={26} /></div>
          <div className="state-title">Couldn&rsquo;t render the board</div>
          <div className="state-body">Something went wrong loading this view. Refresh the page to try again.</div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Helpers ──
const fmtOdds = (o: number | null): string => (o != null && Number.isFinite(o) ? o : 0).toFixed(2);
const kickoffTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) + " GMT" : "—";
function probLabel(p: number | null): { label: string; cls: string } {
  const v = p ?? 0;
  if (v >= 0.62) return { label: "Very High", cls: "conf-very" };
  if (v >= 0.52) return { label: "Strong", cls: "conf-strong" };
  if (v >= 0.42) return { label: "Moderate", cls: "conf-mod" };
  return { label: "Lean", cls: "conf-low" };
}
const pct = (p: number | null): string => (p != null ? `${Math.round(p * 100)}%` : "—");

// ── Masthead ──
function Masthead({ intlCount, clubCount }: { intlCount: number; clubCount: number }): JSX.Element {
  return (
    <div className="masthead">
      <div>
        <div className="mh-eyebrow">Football · The Desk</div>
        <h1 className="mh-title">Goals Galore</h1>
        <p className="mh-sub">The board for goal-fests. Twenty AI analysts and a Moderator vet every
          fixture the model flags to run hot — surfacing the games built to clear <b>more than three goals</b>.
          One read for the internationals, one for the clubs.</p>
      </div>
      <div className="mh-meta">
        <div className="mh-status"><span className="pulse"></span>Model + Swarm · vetted</div>
        <div className="mh-pills">
          <div className="mh-pill"><span className="k">Intl</span><span className="v amber">{intlCount}</span></div>
          <div className="mh-pill"><span className="k">Clubs</span><span className="v">{clubCount}</span></div>
        </div>
      </div>
    </div>
  );
}

// ── Tier switch (BTB seg) ──
function TierSwitch({ tier, setTier, intlCount, clubCount }: { tier: Tier; setTier: (t: Tier) => void; intlCount: number; clubCount: number }): JSX.Element {
  const opts: Array<{ id: Tier; label: string; cnt: number }> = [
    { id: "international", label: "International", cnt: intlCount },
    { id: "club", label: "Clubs", cnt: clubCount },
  ];
  const idx = opts.findIndex((o) => o.id === tier);
  return (
    <div className="tierbar">
      <div className="seg" role="tablist">
        <div className="seg-thumb" style={{ left: `calc(4px + ${idx} * (50% - 4px))`, width: "calc(50% - 4px)" }}></div>
        {opts.map((o) => (
          <button key={o.id} className={tier === o.id ? "on" : ""} role="tab" aria-selected={tier === o.id} onClick={() => setTier(o.id)}>
            {o.label}<span className="cnt">{o.cnt}</span>
          </button>
        ))}
      </div>
      <div className="tier-caption">
        {tier === "international"
          ? <>National teams, UEFA &amp; the <b>World Cup</b> — where goals run hottest.</>
          : <>Club fixtures across the leagues the model reads as <b>high-scoring</b>.</>}
      </div>
    </div>
  );
}

// ── Backing badge — agent-led (≥2 agents saw this many goals) vs model-led ──
function BeliefBadge({ p }: { p: GGPick }): JSX.Element {
  const a = p.believed_agree ?? 0;
  if (a >= 2) return <span className="gg-pb ok">✓ {a} agents see it</span>;
  if (p.panel_status === "confirmed" || p.panel_status === "neutral") return <span className="gg-pb">panel-cleared</span>;
  return <span className="gg-pb dim">model-led</span>;
}

// ── Leg row — shows the believed goal NUMBER for this fixture ──
function oddsFor(p: GGPick): number | null {
  return p.believed_line === "3.5" ? p.over_35_odds : p.believed_line === "2.5" ? p.over_25_odds : p.over_15_odds;
}
function LegRow({ p, rank }: { p: GGPick; rank: number }): JSX.Element {
  const cl = probLabel(p.believed_prob);
  return (
    <div className="leg">
      <div className="leg-rank">{String(rank).padStart(2, "0")}</div>
      <div className="leg-main">
        <div className="leg-fix">{p.fixture_label ?? "—"}</div>
        <div className="leg-sub">
          {p.league && <><span>{p.league}</span><span className="d"></span></>}
          <span className="mono">{kickoffTime(p.kickoff)}</span>
          <span className="d"></span><BeliefBadge p={p} />
        </div>
        <div className="leg-pick">
          <span className="pred">Over {p.believed_line} Goals</span>
          <span className="mkt">{pct(p.believed_prob)} model</span>
        </div>
      </div>
      <div className="leg-odds">
        <div className="o">{fmtOdds(oddsFor(p))}</div>
        <div className={`c mono ${cl.cls}`}>{cl.label}</div>
      </div>
    </div>
  );
}

// ── A goal-number tier = one BTB panel ──
function GoalsPanel({ kicker, title, ranked, picks }: { kicker: string; title: string; ranked: string; picks: GGPick[] }): JSX.Element | null {
  if (picks.length === 0) return null;
  return (
    <div className="panel surface-enter" style={{ marginBottom: 22 }}>
      <div className="panel-head">
        <span className="pk">{kicker}</span>
        <span className="pt">{title}</span>
        <span className="pr">{ranked}</span>
      </div>
      {picks.map((p, i) => <LegRow key={p.fixture_key} p={p} rank={i + 1} />)}
    </div>
  );
}

function TierView({ tier, data }: { tier: Tier; data: GGTier }): JSX.Element {
  if (data.over35.length === 0 && data.over25.length === 0 && data.over15.length === 0) {
    return (
      <div className="state-wrap surface-enter">
        <div className="state-icon"><Ico d={GIC.inbox} size={26} /></div>
        <div className="state-title">{tier === "international" ? "No international goal games live" : "No club goal games live"}</div>
        <div className="state-body">Goals Galore surfaces the goal NUMBER the agents believe each fixture will pour.
          Nothing clears the bar right now — empty by design, never a fabricated pick.</div>
      </div>
    );
  }
  return (
    <div className="surface-enter">
      <GoalsPanel kicker="The Galore" title="4+ goals — Over 3.5"
        ranked="Ranked by conviction" picks={data.over35} />
      <GoalsPanel kicker="Three & up" title="3+ goals — Over 2.5"
        ranked="Ranked by conviction" picks={data.over25} />
      <GoalsPanel kicker="Goals on" title="2+ goals — Over 1.5"
        ranked="Ranked by conviction" picks={data.over15} />
    </div>
  );
}

// ── States ──
function LoadingState(): JSX.Element {
  return (
    <div>
      <div className="tierbar"><div className="sk" style={{ width: 280, height: 46, borderRadius: 8 }}></div></div>
      <div className="panel">
        <div className="sk" style={{ height: 50, margin: 0, borderRadius: 0 }}></div>
        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="sk sk-row" style={{ margin: "8px 16px" }}></div>)}
      </div>
    </div>
  );
}

export function GoalsGalorePage(): JSX.Element {
  const vm = useGoalsGalore();
  const [tab, setTab] = useState<Tier>("international");
  const data = tab === "international" ? vm.international : vm.club;
  const distinct = (t: GGTier): number => new Set([...t.over35, ...t.over25, ...t.over15].map((p) => p.fixture_key)).size;
  const intlCount = distinct(vm.international);
  const clubCount = distinct(vm.club);

  let content: ReactNode;
  if (vm.loading) content = <LoadingState />;
  else if (vm.error) content = (
    <div className="state-wrap">
      <div className="state-icon"><Ico d={GIC.inbox} size={26} /></div>
      <div className="state-title">Goals Galore unavailable</div>
      <div className="state-body">{vm.error}</div>
    </div>
  );
  else content = (
    <>
      <TierSwitch tier={tab} setTier={setTab} intlCount={intlCount} clubCount={clubCount} />
      <TierView tier={tab} data={data} />
    </>
  );

  return (
    <div className="btb-page">
      <GgBoundary>
        <Masthead intlCount={intlCount} clubCount={clubCount} />
        {content}
      </GgBoundary>
    </div>
  );
}
